/**
 * Subservices List Page
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    IconButton,
    Avatar,
    Chip,
} from '@mui/material';
import {
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Image as ImageIcon,
    Visibility as VisibilityIcon,
    ToggleOn as ToggleOnIcon,
    ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable, Column, ConfirmDialog } from '../../components/common';
import {
    useGetSubServicesQuery,
    useDeleteSubServiceMutation,
    useUpdateSubServiceMutation,
} from '../../store/api/servicesApi';
import { useGetServicesQuery } from '../../store/api/servicesApi';
import { SubService } from '../../types/service';
import { formatCurrency } from '../../utils/pricing';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const SubservicesListPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [serviceFilter, setServiceFilter] = useState<number | ''>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [selectedSubservice, setSelectedSubservice] = useState<SubService | null>(null);

    const { data, isLoading, error } = useGetSubServicesQuery({
        service: serviceFilter ? Number(serviceFilter) : undefined,
        search: search || undefined,
        is_active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
        page: page + 1,
        page_size: rowsPerPage,
    });

    const { data: servicesData } = useGetServicesQuery({});

    const [deleteSubService, { isLoading: isDeleting }] = useDeleteSubServiceMutation();
    const [updateSubService] = useUpdateSubServiceMutation();
    const [togglingId, setTogglingId] = useState<number | null>(null);

    const handleToggleActive = async (subservice: SubService) => {
        setTogglingId(subservice.id);
        try {
            const formData = new FormData();
            formData.append('is_active', (!subservice.is_active).toString());
            await updateSubService({ id: subservice.id, data: formData }).unwrap();
            toast.success(
                subservice.is_active
                    ? 'Subservice deactivated successfully'
                    : 'Subservice activated successfully'
            );
        } catch (err: any) {
            toast.error(err?.data?.message || 'Failed to update subservice status');
        } finally {
            setTogglingId(null);
        }
    };

    const handleDelete = async () => {
        if (!selectedSubservice) return;

        try {
            await deleteSubService(selectedSubservice.id).unwrap();
            toast.success(t('subservices.toast.deleted'));
            setDeleteDialog(false);
            setSelectedSubservice(null);
        } catch (err: any) {
            toast.error(err?.data?.message || t('subservices.toast.deleteFailed'));
        }
    };

    const columns: Column<SubService>[] = [
        {
            id: 'icon',
            label: t('subservices.cols.icon'),
            minWidth: 80,
            render: (row) => (
                <Avatar
                    src={row.icon || undefined}
                    alt={row.name}
                    variant="rounded"
                    sx={{ width: 40, height: 40 }}
                >
                    <ImageIcon />
                </Avatar>
            ),
        },
        {
            id: 'name',
            label: t('subservices.cols.nameEn'),
            minWidth: 140,
            render: (row) => (
                <Box sx={{ fontWeight: 600, color: 'primary.main' }}>{row.name}</Box>
            ),
        },
        {
            id: 'name_ar',
            label: t('subservices.cols.nameAr'),
            minWidth: 140,
            render: (row) => (
                <Box dir="rtl" sx={{ color: 'text.primary' }}>
                    {row.name_ar?.trim() ? row.name_ar : '—'}
                </Box>
            ),
        },
        {
            id: 'service_name',
            label: t('subservices.cols.service'),
            minWidth: 150,
        },
        {
            id: 'price',
            label: t('subservices.cols.price'),
            minWidth: 140,
            align: 'right',
            render: (row) => (
                row.uses_dual_pricing
                    ? (
                        <Box>
                            <Box>{formatCurrency(row.specialist_price)}</Box>
                            <Box sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {formatCurrency(row.consultant_price)} {t('subservices.consultantSuffix')}
                            </Box>
                        </Box>
                    )
                    : formatCurrency(row.price)
            ),
        },
        {
            id: 'internal_subservices_count',
            label: t('subservices.cols.internal'),
            minWidth: 100,
            align: 'center',
            render: (row) => (
                <Chip
                    label={row.internal_subservices_count}
                    size="small"
                    color={row.has_internal_subservices ? 'info' : 'default'}
                />
            ),
        },
        {
            id: 'is_active',
            label: t('subservices.cols.status'),
            minWidth: 100,
            render: (row) => (
                <Chip
                    label={row.is_active ? t('subservices.status.ACTIVE') : t('subservices.status.INACTIVE')}
                    size="small"
                    color={row.is_active ? 'success' : 'default'}
                />
            ),
        },
        {
            id: 'created_at',
            label: t('subservices.cols.created'),
            minWidth: 120,
            render: (row) => moment(row.created_at).format('MMM DD, YYYY'),
        },
        {
            id: 'actions',
            label: t('common.actions'),
            minWidth: 150,
            render: (row) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={() => navigate(`/subservices/${row.id}`)}
                        color="info"
                        title={t('subservices.buttons.viewDetails')}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => navigate(`/subservices/${row.id}/edit`)}
                        color="primary"
                        title={t('common.edit')}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleToggleActive(row)}
                        disabled={togglingId === row.id}
                        color={row.is_active ? 'success' : 'default'}
                        title={row.is_active ? 'Deactivate' : 'Activate'}
                    >
                        {row.is_active
                            ? <ToggleOnIcon fontSize="small" />
                            : <ToggleOffIcon fontSize="small" />}
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => {
                            setSelectedSubservice(row);
                            setDeleteDialog(true);
                        }}
                        color="error"
                        title={t('common.delete')}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <PageHeader
                title={t('subservices.title')}
                subtitle={t('subservices.subtitle')}
                breadcrumbs={[
                    { label: t('nav.dashboard'), path: '/' },
                    { label: t('nav.subServices') },
                ]}
                actionLabel={t('subservices.buttons.addSubservice')}
                onAction={() => navigate('/subservices/create')}
            />

            {/* Filters */}
            <Card sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        placeholder={t('subservices.filters.searchSubservices')}
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>{t('subservices.filters.service')}</InputLabel>
                        <Select
                            value={serviceFilter}
                            label={t('subservices.filters.service')}
                            onChange={(e) => setServiceFilter(e.target.value as number | '')}
                        >
                            <MenuItem value="">{t('subservices.filters.allServices')}</MenuItem>
                            {servicesData?.results?.map((service) => (
                                <MenuItem key={service.id} value={service.id}>
                                    {service.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>{t('subservices.filters.status')}</InputLabel>
                        <Select
                            value={statusFilter}
                            label={t('subservices.filters.status')}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <MenuItem value="">{t('subservices.filters.allStatuses')}</MenuItem>
                            <MenuItem value="active">{t('subservices.status.ACTIVE')}</MenuItem>
                            <MenuItem value="inactive">{t('subservices.status.INACTIVE')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Card>

            {/* Table */}
            <DataTable
                columns={columns}
                data={data?.results || []}
                loading={isLoading}
                error={error ? t('subservices.loadError') : null}
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={data?.count || 0}
                onPageChange={setPage}
                onRowsPerPageChange={setRowsPerPage}
                emptyMessage={t('subservices.empty.subservices')}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={deleteDialog}
                title={t('subservices.dialog.deleteTitle')}
                message={t('subservices.dialog.deleteMessage', { name: selectedSubservice?.name })}
                onConfirm={handleDelete}
                onCancel={() => {
                    setDeleteDialog(false);
                    setSelectedSubservice(null);
                }}
                loading={isDeleting}
            />
        </Box>
    );
};

export default SubservicesListPage;

